import {
  computed,
  MaybeRefOrGetter,
  toValue,
} from 'vue'
import {
  ImportJob,
  ImportJobStatus,
} from '~domain/io/ImportJob'


export function useImportJobStatus (jobRef: MaybeRefOrGetter<ImportJob | undefined>) {
  const amountSuccess = computed(() => {
    const job = toValue(jobRef)
    return (job?.amountDone ?? 0) - (job?.amountFailed ?? 0)
  })

  const isError = computed(() => {
    const job = toValue(jobRef)
    return job?.status === ImportJobStatus.FAILED
  })

  const isKilled = computed(() => {
    const job = toValue(jobRef)
    return job?.status === ImportJobStatus.KILLED
  })

  const isDone = computed(() => toValue(jobRef)?.isDone ?? false)

  const isImportingFromEdge = computed(() => {
    // TODO: the 'edge_recording_id' attribute key should be a constant,
    // it is defined by the edge backend
    return !!toValue(jobRef)?.tagAttributes?.edge_recording_id
  })

  const isIncompatible = computed(() => {
    return toValue(jobRef)?.status === ImportJobStatus.INCOMPATIBLE_FORMAT
  })

  const isPartialSuccess = computed(() => {
    const job = toValue(jobRef)
    const isDone = job?.isDone ?? false
    return isDone && !!job?.amountFailed
  })

  const isProgress = computed(() => {
    const status = toValue(jobRef)?.status
    return status === ImportJobStatus.STARTING ||
      status === ImportJobStatus.RUNNING
  })

  const isStopped = computed(() =>
    toValue(jobRef)?.isStopped ?? false)

  const isStopping = computed(() => {
    return toValue(jobRef)?.status === ImportJobStatus.STOPPING
  })

  const isUploading = computed(() => {
    return toValue(jobRef)?.status === ImportJobStatus.UPLOADING
  })

  const isWaiting = computed(() => {
    return isUploading.value && progress.value === 1
  })

  const partialUpload = computed<[number, number]>(() => {
    const job = toValue(jobRef)
    const samplesFailed = job?.amountFailed ?? 0
    const samplesDone = job?.amountDone ?? 0
    const samplesTotal = job?.amountTotal ?? 0

    return isStopped.value
      ? [Math.max(0, samplesTotal - samplesDone - samplesFailed), samplesTotal]
      : [Math.max(0, samplesFailed), samplesDone]
  })

  const progress = computed(() => {
    const job = toValue(jobRef)

    if (!job) return 0

    switch (job.status) {
      case ImportJobStatus.STARTING:
      case ImportJobStatus.RUNNING:
      case ImportJobStatus.STOPPING:
        return job.progress
      case ImportJobStatus.UPLOADING:
        return job.uploadProgress ?? 0
      default:
        return 0
    }
  })

  return {
    amountSuccess,
    isError,
    isDone,
    isImportingFromEdge,
    isIncompatible,
    isKilled,
    isPartialSuccess,
    isProgress,
    isStopped,
    isStopping,
    isUploading,
    isWaiting,
    partialUpload,
    progress,
  }
}
